/*
 * By Noth — <bn-look-carousel>
 * Mouse drag-to-scroll (touch already swipes natively) and the pill dots for
 * sections/bynoth-lifestyle.liquid. No arrow buttons.
 */
if (!customElements.get('bn-look-carousel')) {
  class BnLookCarousel extends HTMLElement {
    #track = null;
    #dots = [];
    #frame = 0;
    #settle = 0;

    connectedCallback() {
      this.#track = this.querySelector('[data-track]');
      if (!this.#track) return;

      this.#dots = [...this.querySelectorAll('[data-dot]')];
      this.#dots.forEach((dot, index) => dot.addEventListener('click', () => this.#goTo(index)));

      this.#track.addEventListener('scroll', this.#schedule, { passive: true });
      this.#track.addEventListener('keydown', this.#handleKey);
      this.#track.addEventListener('pointerdown', this.#dragStart);
      window.addEventListener('resize', this.#schedule, { passive: true });
      window.addEventListener('load', this.#schedule);
      this.#update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.#schedule);
      window.removeEventListener('load', this.#schedule);
      cancelAnimationFrame(this.#frame);
      clearTimeout(this.#settle);
    }

    get #cards() {
      return [...this.#track.querySelectorAll('.bn-look')];
    }

    #goTo(index) {
      const card = this.#cards[index];
      if (!card) return;
      const gutter = parseFloat(getComputedStyle(this.#track).paddingInlineStart) || 0;
      this.#track.scrollTo({ left: card.offsetLeft - gutter, behavior: 'smooth' });
    }

    #current() {
      const track = this.#track;
      const cards = this.#cards;
      if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 2) return cards.length - 1;
      const gutter = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      let best = 0;
      let bestDistance = Infinity;
      cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - gutter - track.scrollLeft);
        if (distance < bestDistance) {
          best = index;
          bestDistance = distance;
        }
      });
      return best;
    }

    #handleKey = (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const next = this.#current() + (event.key === 'ArrowRight' ? 1 : -1);
      this.#goTo(Math.max(0, Math.min(this.#cards.length - 1, next)));
    };

    /* The dots follow the swipe; the height only changes once the swipe has settled, so the
       page never moves while the photos are sliding past. */
    #schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#updateDots());
      clearTimeout(this.#settle);
      this.#settle = setTimeout(() => this.#fit(), 180);
    };

    /* Only the look with tagged products is tall. The carousel stays as short as the photos and
       opens up to show the product list only when you slide to that look, so there is no empty
       gap before the dots on the other photos (on desktop too, where several photos are visible). */
    #fit() {
      const track = this.#track;
      const card = this.#cards[this.#current()];
      const tallest = card ? card.offsetHeight : 0;
      if (!tallest) return;
      if (!track.style.transition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        requestAnimationFrame(() => (track.style.transition = 'height 0.25s ease'));
      }
      if (track.classList.contains('is-dragging')) return;
      track.style.height = `${tallest}px`;
    }

    #update() {
      this.#fit();
      this.#updateDots();
    }

    #updateDots() {
      const current = this.#current();
      this.#dots.forEach((dot, index) => {
        if (index === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* Mouse only: touch and pen already scroll natively. */
    #dragStart = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const track = this.#track;
      const startX = event.clientX;
      const startScroll = track.scrollLeft;
      let moved = false;

      const onMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        if (!moved && Math.abs(delta) > 4) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startScroll - delta;
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (moved) {
          track.classList.remove('is-dragging');
          this.#goTo(this.#current());
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };
  }

  customElements.define('bn-look-carousel', BnLookCarousel);
}
